/**
 * shortcuts-section.tsx — "Phím tắt" (Cài đặt cá nhân), CHỈ XEM.
 *
 * Liệt kê đúng những phím tắt ecdp đang xử lý trong mã (bảng lệnh ⌘K, ô soạn tin ở
 * chat-panel, danh sách hội thoại, trợ lý AI, trình dựng luồng tự động hoá).
 * Chưa hỗ trợ đổi phím — thêm phím mới ở đâu thì nhớ cập nhật danh sách này.
 */
import { i18n } from '@/lib/i18n'
import { AccountRow, AccountSection } from './account-ui'

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
const MOD = isMac ? '⌘' : 'Ctrl'

type Shortcut = { keys: string[][]; label: string; hint?: string }

const GROUPS: { id: string; title: string; items: Shortcut[] }[] = [
  {
    id: 'general',
    get title() { return i18n.t('accountPages:shortcuts.groups.general.title') },
    get items(): Shortcut[] { return [
      { keys: [[MOD, 'K']], get label() { return i18n.t('accountPages:shortcuts.groups.general.cmdK.label') }, get hint() { return i18n.t('accountPages:shortcuts.groups.general.cmdK.hint') } },
    ] },
  },
  {
    id: 'composer',
    get title() { return i18n.t('accountPages:shortcuts.groups.composer.title') },
    get items(): Shortcut[] { return [
      { keys: [['Enter']], get label() { return i18n.t('accountPages:shortcuts.groups.composer.send.label') } },
      { keys: [['Shift', 'Enter']], get label() { return i18n.t('accountPages:shortcuts.groups.composer.newline.label') } },
      { keys: [[MOD, 'V']], get label() { return i18n.t('accountPages:shortcuts.groups.composer.pasteImage.label') }, get hint() { return i18n.t('accountPages:shortcuts.groups.composer.pasteImage.hint') } },
      { keys: [['@']], get label() { return i18n.t('accountPages:shortcuts.groups.composer.mention.label') }, get hint() { return i18n.t('accountPages:shortcuts.groups.composer.mention.hint') } },
      { keys: [['↑'], ['↓']], get label() { return i18n.t('accountPages:shortcuts.groups.composer.mentionNav.label') } },
      { keys: [['Enter'], ['Tab']], get label() { return i18n.t('accountPages:shortcuts.groups.composer.mentionPick.label') } },
      { keys: [['Esc']], get label() { return i18n.t('accountPages:shortcuts.groups.composer.mentionClose.label') } },
    ] },
  },
  {
    id: 'conversations',
    get title() { return i18n.t('accountPages:shortcuts.groups.conversations.title') },
    get items(): Shortcut[] { return [
      { keys: [['Esc']], get label() { return i18n.t('accountPages:shortcuts.groups.conversations.closeSearch.label') } },
      { keys: [['Esc']], get label() { return i18n.t('accountPages:shortcuts.groups.conversations.exitMultiSelect.label') } },
      { keys: [['Enter']], get label() { return i18n.t('accountPages:shortcuts.groups.conversations.sendAi.label') }, get hint() { return i18n.t('accountPages:shortcuts.groups.conversations.sendAi.hint') } },
    ] },
  },
  {
    id: 'automation',
    get title() { return i18n.t('accountPages:shortcuts.groups.automation.title') },
    get items(): Shortcut[] { return [{ keys: [['Esc']], get label() { return i18n.t('accountPages:shortcuts.groups.automation.deselect.label') } }] },
  },
]

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex h-6 min-w-6 items-center justify-center rounded border border-b-2 bg-muted px-1.5 font-sans text-xs font-medium text-foreground">
      {children}
    </kbd>
  )
}

export function ShortcutsSection() {
  return (
    <div className="max-w-3xl space-y-6">
      {GROUPS.map((g) => (
        <AccountSection key={g.id} title={g.title}>
          {g.items.map((s, i) => (
            <AccountRow key={i} label={s.label} hint={s.hint}>
              <div className="flex flex-wrap items-center gap-1.5 lg:justify-end">
                {s.keys.map((combo, ci) => (
                  <span key={ci} className="flex items-center gap-1">
                    {ci > 0 && <span className="px-1 text-xs text-muted-foreground">{i18n.t('accountPages:shortcuts.or')}</span>}
                    {combo.map((k, ki) => (
                      <span key={ki} className="flex items-center gap-1">
                        {ki > 0 && <span className="text-xs text-muted-foreground">+</span>}
                        <Kbd>{k}</Kbd>
                      </span>
                    ))}
                  </span>
                ))}
              </div>
            </AccountRow>
          ))}
        </AccountSection>
      ))}
    </div>
  )
}
